import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Cloud, Database, Download, RefreshCw, Clock, CheckCircle2, AlertCircle,
  FileJson, FileText, FileSpreadsheet, HardDrive, Settings, Loader2,
  Shield, Calendar, File
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function BackupDashboard() {
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggeringBackup, setTriggeringBackup] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [onedriveEmail, setOnedriveEmail] = useState('');
  const [googleEmail, setGoogleEmail] = useState('');
  
  const fetchData = useCallback(async () => {
    try {
      const [statusRes, backupsRes, configRes] = await Promise.all([
        axios.get(`${API}/api/backups/status`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/backups/list`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/backups/config`, { headers: getAuthHeaders() })
      ]);
      
      setStatus(statusRes.data);
      setBackups(backupsRes.data.backups || []);
      setConfig(configRes.data);
      
      if (configRes.data.onedrive?.email) {
        setOnedriveEmail(configRes.data.onedrive.email);
      }
      if (configRes.data.google_sheets?.email) {
        setGoogleEmail(configRes.data.google_sheets.email);
      }
    } catch (error) {
      console.error('Failed to fetch backup data:', error);
      toast.error('Failed to load backup status');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const triggerBackup = async () => {
    setTriggeringBackup(true);
    try {
      const response = await axios.post(`${API}/api/backups/trigger`, {}, { headers: getAuthHeaders() });
      toast.success(`Backup completed! ${response.data.documents_backed_up} documents backed up.`);
      fetchData();
    } catch (error) {
      toast.error('Backup failed. Check logs for details.');
    } finally {
      setTriggeringBackup(false);
    }
  };
  
  const downloadBackup = async (backupId, format) => {
    try {
      const response = await axios.get(
        `${API}/api/backups/download/${backupId}/${format}`,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${backupId}_backup.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${format.toUpperCase()} file downloaded`);
    } catch (error) {
      toast.error(`Failed to download ${format}`);
    }
  };
  
  const saveOneDriveConfig = async () => {
    if (!onedriveEmail) {
      toast.error('Please enter OneDrive email');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/backups/onedrive/configure`,
        { onedrive_email: onedriveEmail },
        { headers: getAuthHeaders() }
      );
      toast.success('OneDrive configured successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to configure OneDrive');
    }
  };
  
  const saveGoogleSheetsConfig = async () => {
    if (!googleEmail) {
      toast.error('Please enter Google email');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/backups/google-sheets/configure`,
        { google_email: googleEmail },
        { headers: getAuthHeaders() }
      );
      toast.success('Google Sheets configured successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to configure Google Sheets');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="backup-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Data Backup Center</h2>
          <p className="text-gray-500">Automatic backups every 8 hours</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Configure
          </button>
          <button
            onClick={triggerBackup}
            disabled={triggeringBackup}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {triggeringBackup ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Backup Now
          </button>
        </div>
      </div>
      
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Backup Interval</p>
              <p className="text-2xl font-bold">Every 8 Hours</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-blue-100 text-sm">
            {status?.scheduler_running ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-green-300" />
                Scheduler Active
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-yellow-300" />
                Scheduler Paused
              </>
            )}
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Database className="w-6 h-6" />
            </div>
            <div>
              <p className="text-green-100 text-sm">Latest Backup</p>
              <p className="text-2xl font-bold">
                {status?.latest_backup?.total_documents?.toLocaleString() || '0'} docs
              </p>
            </div>
          </div>
          <p className="text-green-100 text-sm">
            {status?.latest_backup?.timestamp
              ? new Date(status.latest_backup.timestamp).toLocaleString()
              : 'No backups yet'}
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Cloud className="w-6 h-6" />
            </div>
            <div>
              <p className="text-purple-100 text-sm">Cloud Sync</p>
              <p className="text-2xl font-bold">
                {config?.onedrive?.status === 'configured' ? 'Active' : 'Setup Required'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-purple-100 text-sm">
            <span className="flex items-center gap-1">
              <HardDrive className="w-3 h-3" />
              OneDrive
            </span>
            <span className="flex items-center gap-1">
              <FileSpreadsheet className="w-3 h-3" />
              Google Sheets
            </span>
          </div>
        </div>
      </div>
      
      {/* Configuration Panel */}
      {showConfig && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Backup Configuration
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* OneDrive Config */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                OneDrive Account
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={onedriveEmail}
                  onChange={(e) => setOnedriveEmail(e.target.value)}
                  placeholder="your@outlook.com"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0"
                />
                <button
                  onClick={saveOneDriveConfig}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Backups will sync to: /Jiffys_Backups/
              </p>
            </div>
            
            {/* Google Sheets Config */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Google Sheets Account
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={googleEmail}
                  onChange={(e) => setGoogleEmail(e.target.value)}
                  placeholder="your@gmail.com"
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-0"
                />
                <button
                  onClick={saveGoogleSheetsConfig}
                  className="px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
                >
                  Save
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Transaction data exported to Google Sheets
              </p>
            </div>
          </div>
          
          {/* Export Formats */}
          <div className="pt-4 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-3">Export Formats</p>
            <div className="flex flex-wrap gap-2">
              {['JSON', 'TXT (Notepad)', 'CSV (Excel)', 'XLSX', 'DOCX (Word)'].map((format) => (
                <span
                  key={format}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center gap-1"
                >
                  <File className="w-3 h-3" />
                  {format}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Backup History */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Backup History
          </h3>
          <span className="text-sm text-gray-500">{backups.length} backups</span>
        </div>
        
        <div className="divide-y divide-gray-100">
          {backups.length > 0 ? (
            backups.map((backup) => (
              <div
                key={backup.backup_id}
                className="p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Database className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{backup.backup_id}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{new Date(backup.timestamp).toLocaleString()}</span>
                      <span>{backup.total_documents?.toLocaleString()} documents</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Sync Status */}
                  <div className="flex items-center gap-2 mr-4">
                    {backup.onedrive_sync_status === 'completed' && (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                        <Cloud className="w-3 h-3" />
                        OneDrive
                      </span>
                    )}
                    {backup.google_sheets_sync_status === 'completed' && (
                      <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        <FileSpreadsheet className="w-3 h-3" />
                        Sheets
                      </span>
                    )}
                  </div>
                  
                  {/* Download Buttons */}
                  <button
                    onClick={() => downloadBackup(backup.backup_id, 'json')}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Download JSON"
                  >
                    <FileJson className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadBackup(backup.backup_id, 'txt')}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="Download Notepad"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => downloadBackup(backup.backup_id, 'csv')}
                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                    title="Download Excel/CSV"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p>No backups yet. Click "Backup Now" to create your first backup.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Security Notice */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 flex items-center gap-4">
        <Shield className="w-10 h-10 text-green-600" />
        <div>
          <p className="font-medium text-gray-900">Enterprise-Grade Data Protection</p>
          <p className="text-sm text-gray-600">
            All backups are encrypted and stored securely. Recovery available within seconds in case of system failure.
          </p>
        </div>
      </div>
    </div>
  );
}
